"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-sls-selective-laser-sintering";

const HERO_IMAGE = "/image/new-version/more/SLS/selective-laser-sintering-sls-materials.webp";

const ITEMS = Array.from({ length: 8 }, (_, i) => i + 1);

export default function SlsMaterialOptions() {
	const t = useTranslations(NAMESPACE);
	const [openIndex, setOpenIndex] = useState(0);

	const toggle = (index: number) => {
		setOpenIndex(openIndex === index ? -1 : index);
	};

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-col gap-8 lg:flex-row lg:gap-12 xl:gap-16">
					<div className="flex w-full flex-col lg:w-[380px] lg:shrink-0 xl:w-[420px]">
						<div className="relative aspect-[4/4] w-full overflow-hidden">
							<Image
								src={HERO_IMAGE}
								alt={t("materialOptionsImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 420px"
							/>
						</div>

						<div className="mt-6 lg:mb-20 lg:mt-auto">
							<Link
								href="/contact-us"
								className="inline-flex items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white md:px-8 md:py-3.5 md:text-base"
							>
								{t("materialOptionsContactUs")}
							</Link>
						</div>
					</div>

					<div className="flex-1">
						<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
							{t("materialOptionsTitle")}
						</h2>

						<div className="divide-y divide-gray-200 border-t border-gray-200">
							{ITEMS.map((n) => {
								const isOpen = openIndex === n - 1;
								const title = t(`materialOptionsItem${n}Title` as any);
								const subtitle = t(`materialOptionsItem${n}Subtitle` as any);

								return (
									<div key={n}>
										<button
											onClick={() => toggle(n - 1)}
											className="flex w-full items-center justify-between py-4 text-left"
										>
											<div className="flex items-baseline gap-2">
												<span className="text-base font-bold text-black sm:text-lg">{title}</span>
												{subtitle && <span className="text-sm text-gray-500">({subtitle})</span>}
											</div>
											<span className="flex h-7 w-7 shrink-0 items-center justify-center">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="4"
													strokeLinecap="round"
													strokeLinejoin="round"
													className={`transition-colors ${isOpen ? "text-gray-800" : "text-themePrimary400"}`}
												>
													<line x1="5" y1="12" x2="19" y2="12" />
													{!isOpen && <line x1="12" y1="5" x2="12" y2="19" />}
												</svg>
											</span>
										</button>

										{isOpen && (
											<div className="pb-6 pl-0 pr-8">
												<p className="mb-4 text-sm leading-relaxed text-black md:text-base">
													{t(`materialOptionsItem${n}Desc` as any)}
												</p>

												<h4 className="mb-2 text-base font-bold text-black">
													{t("materialOptionsKeyBenefits")}
												</h4>
												<ul className="mb-4 space-y-1.5">
													<li className="flex items-start gap-2 text-sm text-black md:text-base">
														<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-themePrimary400" />
														{t(`materialOptionsItem${n}Benefit1` as any)}
													</li>
													<li className="flex items-start gap-2 text-sm text-black md:text-base">
														<span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-themePrimary400" />
														{t(`materialOptionsItem${n}Benefit2` as any)}
													</li>
												</ul>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
